import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../useAuth';
import { getAllProfessions, Profession } from '../api/Professions';
import { professionToSlug } from '../utils/professionSlug';
import {
  getStateRequirementsForProfession,
  IStateRequirement,
} from '../api/StateRequirements';
import SEOHelmet from '../components/SEOHelmet';

// ── Fallback official board URLs (used when DB links are empty) ────────────────
// Keys: lowercase state abbreviation, value: [url, label]
const OFFICIAL_BOARD_URLS: Record<string, Record<string, [string, string]>> = {
  ARCH: {
    AL: ['https://www.boa.alabama.gov/', 'Alabama Board of Architects'],
    AZ: ['https://azboa.gov/continuing-education', 'Arizona Board of Technical Registration'],
    CA: ['https://www.cab.ca.gov/lic/ce.shtml', 'California Architects Board'],
    CT: ['https://portal.ct.gov/DCP/License-Services-Division/All-License-Applications/Architect', 'CT DCP Architects'],
    FL: ['https://www.myfloridalicense.com/DBPR/architects/', 'Florida DBPR Architects'],
    GA: ['https://sos.ga.gov/page/architecture', 'Georgia Secretary of State'],
    IL: ['https://idfpr.illinois.gov/profs/arch.asp', 'Illinois IDFPR Architecture'],
    MA: ['https://www.mass.gov/orgs/board-of-registration-of-architects', 'MA Board of Architects'],
    NJ: ['https://www.njconsumeraffairs.gov/arch/', 'NJ Board of Architects'],
    NY: ['https://www.op.nysed.gov/professions/architects/', 'NY State Education Dept'],
    PA: ['https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Architects/Pages/default.aspx', 'PA State Architects Board'],
    TX: ['https://www.tbae.texas.gov/', 'Texas Board of Architectural Examiners'],
    WA: ['https://app.leg.wa.gov/rcw/default.aspx?cite=18.08', 'Washington State Architects'],
  },
  PE: {
    AL: ['https://www.bels.alabama.gov/', 'Alabama BELS'],
    AZ: ['https://azboa.gov/', 'Arizona Board of Technical Registration'],
    CA: ['https://www.bpelsg.ca.gov/', 'CA Board for Professional Engineers'],
    CT: ['https://portal.ct.gov/DCP/License-Services-Division/All-License-Applications/Professional-Engineer', 'CT DCP Engineers'],
    FL: ['https://www.myfloridalicense.com/DBPR/professional-engineers/', 'Florida DBPR Engineers'],
    GA: ['https://sos.ga.gov/page/professional-engineers', 'Georgia PE Board'],
    IL: ['https://idfpr.illinois.gov/profs/pe.asp', 'Illinois IDFPR PE'],
    MA: ['https://www.mass.gov/orgs/board-of-registration-of-professional-engineers', 'MA Board of PE'],
    NJ: ['https://www.njconsumeraffairs.gov/pels/', 'NJ PE Board'],
    NY: ['https://www.op.nysed.gov/professions/engineers/', 'NY State Education Dept'],
    PA: ['https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/EngineersLandSurveyorsGeologists/Pages/default.aspx', 'PA Engineers Board'],
    TX: ['https://pels.texas.gov/', 'Texas PE Board'],
    WA: ['https://app.leg.wa.gov/rcw/default.aspx?cite=18.43', 'Washington PE Board'],
  },
  CPA: {
    AL: ['https://asbpa.alabama.gov/', 'Alabama Society of CPAs'],
    AZ: ['https://azaccountancy.gov/', 'Arizona State Board of Accountancy'],
    CA: ['https://www.calcpa.org/', 'California Board of Accountancy'],
    CT: ['https://portal.ct.gov/DCP/License-Services-Division/All-License-Applications/Certified-Public-Accountant', 'CT DCP CPA'],
    FL: ['https://floridasaccountancy.gov/', 'Florida Board of Accountancy'],
    GA: ['https://sos.ga.gov/page/state-board-of-accountancy', 'Georgia Board of Accountancy'],
    IL: ['https://idfpr.illinois.gov/profs/CPA.asp', 'Illinois IDFPR CPA'],
    MA: ['https://www.mass.gov/orgs/board-of-public-accountancy', 'MA Board of Public Accountancy'],
    NJ: ['https://www.njconsumeraffairs.gov/accountancy/', 'NJ State Board of Accountancy'],
    NY: ['https://www.op.nysed.gov/certified-public-accountants', 'NY State Education Dept'],
    PA: ['https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/Accountancy/Pages/default.aspx', 'PA State Board of Accountancy'],
    TX: ['https://www.tsbpa.texas.gov/', 'Texas State Board of Public Accountancy'],
    WA: ['https://acb.wa.gov/', 'Washington State Board of Accountancy'],
  },
  LA: {
    AL: ['https://www.blsa.alabama.gov/', 'Alabama Board of Landscape Architects'],
    AZ: ['https://azboa.gov/', 'Arizona Board of Technical Registration'],
    CA: ['https://www.clsa.org/', 'California Land Surveyors Association'],
    CT: ['https://portal.ct.gov/DCP/License-Services-Division/All-License-Applications/Landscape-Architect', 'CT Landscape Architects'],
    FL: ['https://www.myfloridalicense.com/DBPR/landscape-architecture/', 'Florida DBPR LA'],
    GA: ['https://sos.ga.gov/page/landscape-architects', 'Georgia LA Board'],
    IL: ['https://idfpr.illinois.gov/profs/la.asp', 'Illinois IDFPR LA'],
    MA: ['https://www.mass.gov/orgs/board-of-registration-of-landscape-architects', 'MA Board of LA'],
    NJ: ['https://www.njconsumeraffairs.gov/la/', 'NJ LA Board'],
    NY: ['https://www.op.nysed.gov/professions/landscape-architects/', 'NY State Education Dept'],
    PA: ['https://www.dos.pa.gov/ProfessionalLicensing/BoardsCommissions/LandscapeArchitects/Pages/default.aspx', 'PA LA Board'],
    TX: ['https://www.tbae.texas.gov/', 'Texas TBAE'],
    WA: ['https://app.leg.wa.gov/rcw/default.aspx?cite=18.96', 'Washington LA Board'],
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATE_ABBR: Record<string, string> = {
  Alabama:'AL', Alaska:'AK', Arizona:'AZ', Arkansas:'AR', California:'CA',
  Colorado:'CO', Connecticut:'CT', Delaware:'DE', Florida:'FL', Georgia:'GA',
  Hawaii:'HI', Idaho:'ID', Illinois:'IL', Indiana:'IN', Iowa:'IA',
  Kansas:'KS', Kentucky:'KY', Louisiana:'LA', Maine:'ME', Maryland:'MD',
  Massachusetts:'MA', Michigan:'MI', Minnesota:'MN', Mississippi:'MS', Missouri:'MO',
  Montana:'MT', Nebraska:'NE', Nevada:'NV', 'New Hampshire':'NH', 'New Jersey':'NJ',
  'New Mexico':'NM', 'New York':'NY', 'North Carolina':'NC', 'North Dakota':'ND',
  Ohio:'OH', Oklahoma:'OK', Oregon:'OR', Pennsylvania:'PA', 'Rhode Island':'RI',
  'South Carolina':'SC', 'South Dakota':'SD', Tennessee:'TN', Texas:'TX',
  Utah:'UT', Vermont:'VT', Virginia:'VA', Washington:'WA', 'West Virginia':'WV',
  Wisconsin:'WI', Wyoming:'WY',
};

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function cycleLabel(req: IStateRequirement): string {
  if (req.hasFixedCycleDates && req.fixedCycleStartMonth && req.fixedCycleEndMonth) {
    const start = MONTH_NAMES[(req.fixedCycleStartMonth ?? 1) - 1];
    const end   = MONTH_NAMES[(req.fixedCycleEndMonth   ?? 12) - 1];
    return `${start} – ${end} (${req.cycleLengthYears}-yr cycle)`;
  }
  return `${req.cycleLengthYears}-year rolling cycle`;
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

function getOfficialUrl(profId: string, abbr: string): [string, string] | null {
  return OFFICIAL_BOARD_URLS[profId.toUpperCase()]?.[abbr.toUpperCase()] ?? null;
}

// ── Sub-components ────────────────────────────────────────────────────────────
const StatPill = ({ label, value, accent = 'var(--color-accent)' }: { label: string; value: string; accent?: string }) => (
  <div style={{
    padding: '0.875rem 1.25rem',
    background: 'var(--color-surface-muted)',
    border: `1px solid ${accent}22`,
    borderTop: `3px solid ${accent}`,
    borderRadius: 'var(--radius-md)',
    minWidth: '130px',
    flex: '1',
  }}>
    <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>{label}</div>
    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xl)', color: 'var(--color-text)', lineHeight: 1 }}>{value}</div>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const ProfessionLanding: React.FC = () => {
  const { professionSlug } = useParams<{ professionSlug: string }>();
  const { user } = useAuth();
  const ctaTo = user ? '/dashboard' : '/register';

  // undefined = still loading, null = not found
  const [profession, setProfession] = useState<Profession | null | undefined>(undefined);
  const [allProfessions, setAllProfessions] = useState<Profession[]>([]);
  const [requirements, setRequirements] = useState<IStateRequirement[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(false);
  const [activeStateId, setActiveStateId] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const navRef = useRef<HTMLDivElement>(null);

  // Load profession from DB by matching slug derived from name
  useEffect(() => {
    getAllProfessions()
      .then(all => {
        setAllProfessions(all);
        const match = all.find(
          p => p.isActive !== false &&
               professionToSlug(p.name) === professionSlug
        );
        setProfession(match ?? null);
      })
      .catch(() => setProfession(null));
  }, [professionSlug]);

  useEffect(() => {
    if (profession === undefined) return; // still loading profession list
    if (!profession) { setLoading(false); return; }
    setLoading(true);
    setError(false);
    getStateRequirementsForProfession(profession.professionID)
      .then(data => {
        const sorted = [...data].sort((a, b) =>
          (a.stateName ?? '').localeCompare(b.stateName ?? '')
        );
        setRequirements(sorted);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [profession?.professionID]);

  useEffect(() => {
    if (!requirements.length) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveStateId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [requirements]);

  const scrollToState = (id: string) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const mainNavHeight = 64; // --navbar-height CSS variable value
    const bookmarkBarHeight = navRef.current?.offsetHeight ?? 0;
    const top = el.getBoundingClientRect().top + window.scrollY - mainNavHeight - bookmarkBarHeight - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  // Still fetching the profession list
  if (profession === undefined) return null;

  if (profession === null) {
    return (
      <>
        <SEOHelmet noindex={true} title="Profession Not Found | CEC Track" />
        <div style={{ padding: '5rem 1.5rem', textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-3xl)', color: 'var(--color-text)' }}>Profession Not Found</h1>
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)', marginBottom: '2rem' }}>The profession you're looking for doesn't exist.</p>
          <Link to="/" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)' }}>← Back to Home</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHelmet
        title={`${profession.name} CE Requirements by State | CEC Track`}
        description={`Continuing education requirements for ${profession.name}s across all states. Total credits, cycle lengths, mandatory categories, and renewal deadlines.`}
        keywords={`${profession.name} continuing education, ${profession.name} CE requirements, ${profession.name} license renewal, professional CE credits`}
        canonical={`https://cectrack.com/profession/${professionSlug}`}
        noindex={!loading && requirements.length === 0}
      />

      {/* ── Hero ── */}
      <section style={{
        background: 'var(--color-primary)',
        padding: '5rem 0 4rem',
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <Link to="/" style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Home</Link>
            <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 0.5rem' }}>/</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-accent)' }}>{profession.name}</span>
          </div>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.5rem' }}>
            CE Requirements by State
          </p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', color: '#fff', lineHeight: 1.05, marginBottom: '0.75rem', letterSpacing: '-0.01em' }}>
            {profession.name}
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'rgba(255,255,255,0.65)', marginBottom: '2rem', maxWidth: '560px' }}>
            Credits, cycle lengths, mandatory categories, and renewal rules — every state in one place.
          </p>
          {!loading && !error && (
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-map" style={{ color: 'var(--color-accent)' }} />
                {requirements.length} {requirements.length === 1 ? 'state' : 'states'} in system
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-arrow-clockwise" style={{ color: 'var(--color-accent)' }} />
                Updated from live database
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Sticky anchor nav ── */}
      {!loading && requirements.length > 0 && (
        <div ref={navRef} style={{
          position: 'sticky', top: 'var(--navbar-height)', zIndex: 100,
          background: 'var(--color-surface)',
          borderBottom: '2px solid var(--color-border)',
        }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', padding: '0.75rem 0' }}>
              {requirements.map(req => {
                const id = slugify(req.stateName ?? String(req.stateID));
                const abbr = req.stateAbbreviation || STATE_ABBR[req.stateName ?? ''] || req.stateName?.slice(0, 2).toUpperCase() || '??';
                const isActive = activeStateId === id;
                return (
                  <button
                    key={id}
                    onClick={() => scrollToState(id)}
                    title={req.stateName}
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontWeight: isActive ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                      fontSize: '0.7rem',
                      padding: '0.35rem 0.65rem',
                      borderRadius: 'var(--radius-sm)',
                      border: isActive ? '1.5px solid var(--color-primary)' : '1px solid var(--color-border)',
                      background: isActive ? 'var(--color-primary)' : 'transparent',
                      color: isActive ? '#fff' : 'var(--color-text-muted)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all var(--transition-fast)',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {abbr}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ background: 'var(--color-surface)', minHeight: '60vh' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '3rem 1.5rem' }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '5rem 0' }}>
              <div className="spinner-border" style={{ color: 'var(--color-accent)', width: '2.5rem', height: '2.5rem' }} role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)', marginTop: '1rem', fontSize: 'var(--fs-sm)' }}>Loading state requirements…</p>
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '5rem 0' }}>
              <i className="bi bi-exclamation-circle" style={{ fontSize: '2.5rem', color: 'var(--color-danger)', display: 'block', marginBottom: '1rem' }} />
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)', marginBottom: '1.5rem' }}>
                Could not load state requirements. Please try again.
              </p>
              <Link to={ctaTo} style={{ fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-accent)', textDecoration: 'none' }}>
                Create a free account to track your CE →
              </Link>
            </div>
          )}

          {!loading && !error && requirements.length === 0 && (
            <div style={{ textAlign: 'center', padding: '5rem 0' }}>
              <i className="bi bi-database-x" style={{ fontSize: '2.5rem', color: 'var(--color-text-muted)', display: 'block', marginBottom: '1rem' }} />
              <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-muted)' }}>
                No state requirements found for {profession.name} yet.
              </p>
            </div>
          )}

          {/* ── Credential naming advisories (stateNotes) ── */}
          {!loading && !error && profession.stateNotes && profession.stateNotes.length > 0 && (() => {
            // Only show notes that have a redirectProfessionID (cross-profession naming conflicts)
            const redirectNotes = profession.stateNotes!.filter(n => n.redirectProfessionID);
            if (!redirectNotes.length) return null;

            // Build a map of stateID → abbreviation from the requirements we already have
            const stateAbbrMap: Record<number, string> = {};
            requirements.forEach(r => {
              if (r.stateID && r.stateAbbreviation) stateAbbrMap[r.stateID] = r.stateAbbreviation;
            });

            return redirectNotes.map((note, i) => {
              const stateLabels = note.stateIDs
                .map(sid => stateAbbrMap[sid])
                .filter(Boolean)
                .slice(0, 8); // show up to 8 abbreviations, then "…"
              const overflow = note.stateIDs.length - stateLabels.length;
              const redirectProf = allProfessions.find(p => p.professionID === note.redirectProfessionID);
              const redirectSlug = redirectProf ? professionToSlug(redirectProf.name) : professionToSlug(note.redirectProfessionID!);

              return (
                <div
                  key={i}
                  style={{
                    display: 'flex', gap: '1rem', alignItems: 'flex-start',
                    padding: '1rem 1.25rem',
                    marginBottom: '1.5rem',
                    background: 'rgba(245, 158, 11, 0.06)',
                    border: '1px solid rgba(245, 158, 11, 0.35)',
                    borderLeft: '4px solid #f59e0b',
                    borderRadius: 'var(--radius-md)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--color-text)',
                  }}
                >
                  <span style={{ fontSize: '1.1rem', flexShrink: 0, lineHeight: 1.4 }}>⚠️</span>
                  <div>
                    <strong>Different credential name in some states</strong>
                    <div style={{ marginTop: '0.3rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                      {note.message.split('.')[0]}.{' '}
                      {stateLabels.length > 0 && (
                        <>
                          Affected states:{' '}
                          {stateLabels.map(a => (
                            <span key={a} style={{
                              display: 'inline-block', fontFamily: 'var(--font-mono)',
                              fontSize: '0.7rem', fontWeight: 700,
                              background: 'rgba(245,158,11,0.15)', color: '#7c4a00',
                              borderRadius: 4, padding: '1px 6px', marginRight: 4,
                            }}>{a}</span>
                          ))}
                          {overflow > 0 && <span style={{ color: 'var(--color-text-muted)' }}>+{overflow} more</span>}
                        </>
                      )}
                    </div>
                    <Link
                      to={`/profession/${redirectSlug}`}
                      style={{
                        display: 'inline-block', marginTop: '0.5rem',
                        fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-xs)',
                        color: '#7c4a00', textDecoration: 'underline',
                      }}
                    >
                      View {redirectProf?.name ?? note.redirectProfessionID} requirements →
                    </Link>
                  </div>
                </div>
              );
            });
          })()}

          {/* ── State sections ── */}
          {!loading && !error && requirements.map((req, idx) => {
            const stateName = req.stateName ?? String(req.stateID);
            const abbr = req.stateAbbreviation || STATE_ABBR[stateName] || stateName.slice(0, 2).toUpperCase();
            const id = slugify(stateName);
            const term = req.creditTerminology ?? 'Credits';
            const hasCategories = req.categoryIDs && req.categoryIDs.some(cat => (cat.minCredits ?? 0) > 0);
            const isNoCLE = req.totalCreditsRequired === 0;

            // Official URL: DB value takes priority, static fallback otherwise
            const fallback = getOfficialUrl(profession.professionID, abbr);
            const link1 = req.stateprofessionlink1 || (fallback ? fallback[0] : null);
            const link1desc = req.stateprofessionlink1desc || (fallback ? fallback[1] : 'Official Board');
            const link2 = req.stateprofessionlink2;
            const link2desc = req.stateprofessionlink2desc || 'Additional Resource';

            return (
              <section
                key={req._id ?? idx}
                id={id}
                ref={el => { sectionRefs.current[id] = el; }}
                style={{
                  marginBottom: '2.5rem',
                  background: 'var(--color-surface-raised)',
                  border: '1px solid var(--color-border)',
                  borderLeft: `4px solid ${isNoCLE ? 'var(--color-border)' : 'var(--color-primary)'}`,
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-card)',
                  overflow: 'hidden',
                  scrollMarginTop: '140px',
                  opacity: isNoCLE ? 0.75 : 1,
                }}
              >
                {/* State header */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '1.25rem 1.5rem',
                  borderBottom: '1px solid var(--color-border)',
                  background: 'var(--color-surface-raised)',
                  flexWrap: 'wrap', gap: '0.75rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {/* State abbreviation badge */}
                    <div style={{
                      width: '56px', height: '56px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--color-primary)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexShrink: 0,
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 700,
                        fontSize: abbr.length > 2 ? '0.65rem' : '0.875rem',
                        color: '#fff',
                        letterSpacing: '0.04em',
                      }}>
                        {abbr}
                      </span>
                    </div>
                    {/* State name — big and primary */}
                    <div>
                      <h2 style={{
                        fontFamily: 'var(--font-display)',
                        fontWeight: 700,
                        fontSize: 'clamp(1.6rem, 3vw, 2.25rem)',
                        color: 'var(--color-text)',
                        margin: 0,
                        lineHeight: 1.1,
                        letterSpacing: '-0.01em',
                      }}>
                        {stateName}
                      </h2>
                      <div style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--color-text-muted)',
                        marginTop: '0.25rem',
                      }}>
                        {profession.name} — License Renewal Requirements
                      </div>
                    </div>
                  </div>

                  {/* Reference links */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {link1 && (
                      <a href={link1} target="_blank" rel="noopener noreferrer" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
                        color: 'var(--color-accent)', textDecoration: 'none',
                        padding: '0.4rem 0.875rem', border: '1px solid var(--color-accent)',
                        borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap',
                        transition: 'background var(--transition-fast), color var(--transition-fast)',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--color-accent)'; }}
                      >
                        <i className="bi bi-box-arrow-up-right" />
                        {link1desc}
                      </a>
                    )}
                    {link2 && (
                      <a href={link2} target="_blank" rel="noopener noreferrer" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
                        color: 'var(--color-text-muted)', textDecoration: 'none',
                        padding: '0.4rem 0.875rem', border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap',
                        transition: 'border-color var(--transition-fast), color var(--transition-fast)',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-text-muted)'; e.currentTarget.style.color = 'var(--color-text)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
                      >
                        <i className="bi bi-box-arrow-up-right" />
                        {link2desc}
                      </a>
                    )}
                  </div>
                </div>

                {/* No mandatory CLE notice */}
                {isNoCLE ? (
                  <div style={{ padding: '1.25rem 1.5rem' }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: 'var(--color-surface-muted)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-full)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--fs-sm)',
                      fontWeight: 'var(--fw-semibold)',
                      color: 'var(--color-text-muted)',
                    }}>
                      <i className="bi bi-dash-circle" />
                      No mandatory CLE requirement in this state
                    </div>
                    {req.cycleDateNotes && (
                      <div style={{ marginTop: '0.75rem', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                        <i className="bi bi-info-circle me-1" />{req.cycleDateNotes}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Stat pills row */}
                    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', gap: '0.875rem', flexWrap: 'wrap' }}>
                      <StatPill label={`Total ${term}`}   value={String(req.totalCreditsRequired)} accent="var(--color-primary)" />
                      <StatPill label="Cycle Length"      value={`${req.cycleLengthYears} yr`}    accent="var(--color-accent)" />
                      <StatPill label="Renewal Period"    value={cycleLabel(req)}                  accent="var(--color-accent-warm)" />
                      {req.allowCarryover && (
                        <StatPill label="Carryover Max" value={req.carryoverMaxCredits ? `${req.carryoverMaxCredits} ${term}` : 'Yes'} accent="var(--color-success)" />
                      )}
                      {req.selfStudyMaxPercent != null && (
                        <StatPill label="Self-Study Max" value={`${req.selfStudyMaxPercent}%`} accent="var(--color-text-muted)" />
                      )}
                    </div>

                    {/* Category requirements */}
                    {hasCategories && (
                      <div style={{ padding: '0 1.5rem 1.25rem' }}>
                        <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.625rem' }}>
                          Category Requirements
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {req.categoryIDs.filter(cat => (cat.minCredits ?? 0) > 0).map((cat, ci) => {
                            const isHSW    = cat.categoryTag?.toUpperCase().includes('HSW');
                            const isEthics = cat.categoryTag?.toUpperCase().includes('ETHIC');
                            const tagColor = isHSW ? 'var(--color-accent)' : isEthics ? 'var(--color-accent-warm)' : 'var(--color-text-muted)';
                            const tagBg    = isHSW ? 'var(--color-accent-light)' : isEthics ? 'var(--color-accent-warm-light)' : 'var(--color-surface-muted)';
                            return (
                              <div key={ci} style={{
                                padding: '0.4rem 0.875rem',
                                background: tagBg,
                                border: `1px solid ${tagColor}44`,
                                borderRadius: 'var(--radius-full)',
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                              }}>
                                <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: tagColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  {cat.categoryTag}
                                </span>
                                {cat.minCredits != null && (
                                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-text)', fontWeight: 'var(--fw-semibold)' }}>
                                    {cat.minCredits}
                                    {cat.maxCredits != null && cat.maxCredits !== cat.minCredits ? `–${cat.maxCredits}` : ''}
                                    {' '}{term}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Carryover / cycle notes row */}
                    {(req.allowCarryover || req.cycleDateNotes) && (
                      <div style={{ padding: '0 1.5rem 1.25rem', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                        {req.allowCarryover && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-success)' }}>
                            <i className="bi bi-check-circle-fill" />
                            Carryover allowed{req.carryoverMaxCredits ? ` (max ${req.carryoverMaxCredits} ${term})` : ''}
                          </div>
                        )}
                        {req.cycleDateNotes && (
                          <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                            <i className="bi bi-calendar-range me-1" />{req.cycleDateNotes}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {req.notes && (
                      <div style={{
                        margin: '0 1.5rem 1.25rem',
                        padding: '0.75rem 1rem',
                        background: 'var(--color-surface-muted)',
                        border: '1px solid var(--color-border)',
                        borderLeft: '3px solid var(--color-accent-warm)',
                        borderRadius: 'var(--radius-sm)',
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--color-text-muted)',
                        lineHeight: 1.65,
                      }}>
                        <i className="bi bi-info-circle me-2" style={{ color: 'var(--color-accent-warm)' }} />
                        {req.notes}
                      </div>
                    )}
                  </>
                )}
              </section>
            );
          })}

          {/* CTA */}
          {!loading && !error && requirements.length > 0 && (
            <div style={{
              marginTop: '3rem', padding: '2.5rem',
              background: 'var(--color-primary)',
              borderRadius: 'var(--radius-lg)',
              textAlign: 'center',
            }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-2xl)', color: '#fff', marginBottom: '0.75rem' }}>
                Ready to track your {profession.name} CE credits?
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.65)', marginBottom: '1.5rem' }}>
                CEC Track automatically calculates compliance across all categories and states — for free.
              </p>
              <Link
                to={ctaTo}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.75rem 2rem',
                  background: 'var(--color-accent)', color: 'var(--color-primary)',
                  fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-base)',
                  borderRadius: 'var(--radius-md)', textDecoration: 'none',
                  transition: 'background var(--transition-fast)',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-accent-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-accent)')}
              >
                Get Started Free <i className="bi bi-arrow-right" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfessionLanding;
